// ft3d-hud/log.ts — the BG3-style three-state log.
//
// Off / Fading / Always-on, driven by the `ft-roll-authorized` document event. This surface owns no
// roll model, opens no socket and never calls openSideLog(); it projects rolls and their existing
// reveal controls after handleRollInsert() authorized them.

import type { FtLogActor, FtLogAdvDisSource, FtLogAdvDisTrace, FtLogContest, FtLogEntry, FtLogMode, FtLogSegment } from './types';
import { projectAuthorizedRoll, projectRenderedGroup } from './project-roll';

const MODE_KEY = 'ft3d.hud.log';
/** The rejected roll dock's on/off preference, folded into the three-state setting. */
const LEGACY_FEED_KEY = 'ft3d.campaign.eventFeed';

export const LOG_MODES: FtLogMode[] = ['off', 'fade', 'on'];
export const LOG_MODE_LABEL: Record<FtLogMode, string> = {
    off: 'Off',
    fade: 'Fading',
    on: 'Always on',
};
/** Glyph key handed to iconMap() for each state. */
const MODE_ICON: Record<FtLogMode, string> = {
    off: 'close_sidelog',
    fade: 'log_fading',
    on: 'view_log',
};

// BG3 keeps a line up for only a few seconds. 14s made fading feel like a second always-on.
const FADE_LINE_MS = 5000;
/** Must match the .is-leaving transition in 97-ft3d-hud.css, or the row is cut off mid-fade. */
const FADE_OUT_MS = 700;
const FADE_MAX_LINES = 6;
const STACK_MAX_LINES = 120;
/** How much history to lift out of the legacy log at mount. */
const SEED_MAX_ROWS = 40;

let mode: FtLogMode = 'fade';
let pinned = false;
let logEl: HTMLElement | null = null;
let toggleEl: HTMLElement | null = null;
const listeners = new Set<(next: FtLogMode) => void>();
const entryByLine = new WeakMap<HTMLElement, FtLogEntry>();

function readMode(): FtLogMode {
    try {
        const stored = window.localStorage.getItem(MODE_KEY);
        if (stored && (LOG_MODES as string[]).indexOf(stored) >= 0) return stored as FtLogMode;
        // One-time migration off the superseded dock preference.
        if (window.localStorage.getItem(LEGACY_FEED_KEY) === 'off') return 'off';
        return 'fade';
    } catch {
        // Private windows and blocked site data throw on read; default to the quiet-but-visible state.
        return 'fade';
    }
}

function writeMode(next: FtLogMode): void {
    try { window.localStorage.setItem(MODE_KEY, next); } catch { /* viewer-local nicety only */ }
}

function renderToggleGlyph(): void {
    if (!toggleEl) return;
    const label = `Log — ${LOG_MODE_LABEL[mode].toLowerCase()}`;
    toggleEl.setAttribute('aria-label', label);
    toggleEl.setAttribute('title', `${label} (click to cycle)`);
    toggleEl.setAttribute('data-ft3d-log-state', mode);

    // Rim-hosted (my_campaign's minimap collar): the bezel draws its own glyph from
    // data-ft3d-action-icon, so hand it the state's icon and ask for a redraw rather than writing
    // markup into a slot the rim does not use.
    if (toggleEl.hasAttribute('data-ft3d-action-icon')) {
        toggleEl.setAttribute('data-ft3d-action-icon', MODE_ICON[mode]);
        toggleEl.setAttribute('data-ft3d-action-label', label);
        const rerender = (window as unknown as { ftRenderFt3dActionIcons?: () => void }).ftRenderFt3dActionIcons;
        if (typeof rerender === 'function') rerender();
        return;
    }

    const glyph = toggleEl.querySelector<HTMLElement>('[data-ft3d-button-glyph]');
    const iconMapFn = (window as unknown as { iconMap?: (k: string, w: number, h: number, t: string) => string }).iconMap;
    if (!glyph || typeof iconMapFn !== 'function') return;
    glyph.innerHTML = iconMapFn(MODE_ICON[mode], 22, 22, label);
}

/**
 * The ROLL lines only. The stream is shared with table chat (ft3d-hud/chat.ts appends `.is-chat` lines
 * into this same element so speech and events interleave by time), and every sweep this module does —
 * trimming, expiring, grouping, the off-mode purge — owns the roll half and must leave chat alone.
 */
function rollLines(): HTMLElement[] {
    if (!logEl) return [];
    return Array.from(logEl.querySelectorAll<HTMLElement>('.ft3d-hud__line:not(.is-chat)'));
}

/** The shared line host, so the chat surface can append into the same stream. */
export function ft3dLogLineHost(): HTMLElement | null {
    return logEl;
}

function clearExpiry(line: HTMLElement): void {
    const timer = (line as unknown as { __ft3dExpiry?: number }).__ft3dExpiry;
    if (timer !== undefined) {
        window.clearTimeout(timer);
        delete (line as unknown as { __ft3dExpiry?: number }).__ft3dExpiry;
    }
}

function scheduleExpiry(line: HTMLElement): void {
    clearExpiry(line);
    (line as unknown as { __ft3dExpiry?: number }).__ft3dExpiry =
        window.setTimeout(() => expireLine(line), FADE_LINE_MS);
}

function expireLine(line: HTMLElement): void {
    if (!line.isConnected || mode !== 'fade') return;
    if (pinned) {
        // Keep it while the reader is actually reading; retry once they leave.
        (line as unknown as { __ft3dExpiry?: number }).__ft3dExpiry = window.setTimeout(() => expireLine(line), 2000);
        return;
    }
    // ⛔ Drop is-shown as well. The stacked-opacity rules that make older lines recede are keyed on
    // .is-shown and are MORE specific than .is-leaving, so leaving it on meant they kept winning:
    // the row held its opacity for the whole "fade" and then vanished on removal — a blink, not a
    // fade. This is what makes the fade actually visible.
    line.classList.remove('is-shown');
    line.classList.add('is-leaving');
    window.setTimeout(() => {
        // Re-check: the viewer may have switched to always-on during the leaving animation, and a
        // line that just joined a persistent stack must not be swept away by an old timer.
        if (mode === 'fade' && line.classList.contains('is-leaving')) {
            disposeTooltips(line);
            line.remove();
            refreshAllGroups();
        }
    }, FADE_OUT_MS);
}

function applyMode(): void {
    if (logEl) {
        logEl.setAttribute('data-ft3d-log-mode', mode);
        // ⛔ The element itself is no longer hidden, and it is no longer emptied: since 2026-09-16 it
        // also carries the CHAT lines, and "the log is off" must never take speech away with it. Off
        // drops the ROLL lines and CSS hides any that arrive; chat is untouched.
        logEl.hidden = false;
        if (mode === 'off') {
            rollLines().forEach((line) => {
                clearExpiry(line);
                disposeTooltips(line);
                line.remove();
            });
        }
        if (mode === 'on') {
            // Entering always-on: cancel the pending expiries so the backlog stops evaporating.
            rollLines().forEach((line) => {
                clearExpiry(line);
                // A line caught mid-fade would otherwise vanish from the stack it just joined —
                // and it also lost is-shown on the way out, so give that back or it stays invisible.
                line.classList.remove('is-leaving');
                line.classList.add('is-shown');
            });
            seedOnDemand();
            refreshAllGroups();
            logEl.scrollTop = logEl.scrollHeight;
        }
        if (mode === 'fade') {
            // Always-on condenses shared-roll events. Restore their children before the ticker's
            // per-line expiry/opacity rules take over, while retaining the familiar blue bracket.
            refreshAllGroups();
            // Entering fading FROM always-on: the stack can hold up to STACK_MAX_LINES lines that
            // were never given an expiry. Trim to the fading cap and start their timers, or they
            // sit on screen forever while only new lines come and go.
            const lines = rollLines();
            lines.slice(0, Math.max(0, lines.length - FADE_MAX_LINES)).forEach((line) => {
                clearExpiry(line);
                disposeTooltips(line);
                line.remove();
            });
            rollLines().forEach(scheduleExpiry);
        }
    }
    renderToggleGlyph();
    // Isolated: one throwing subscriber must not stop the rest from updating, or a second control
    // silently drifts out of step with the first.
    listeners.forEach((fn) => { try { fn(mode); } catch { /* presentation only */ } });
}

export function getLogMode(): FtLogMode {
    return mode;
}

export function setLogMode(next: FtLogMode): void {
    if (LOG_MODES.indexOf(next) < 0 || next === mode) return;
    mode = next;
    writeMode(mode);
    applyMode();
}

export function cycleLogMode(): void {
    const next = LOG_MODES[(LOG_MODES.indexOf(mode) + 1) % LOG_MODES.length];
    setLogMode(next === undefined ? 'off' : next);
}

/**
 * Notified on every mode change, so a second control (the Settings tab) can stay in step. Returns
 * an unsubscribe so a caller that re-renders its control does not leave the old one in the set.
 */
export function subscribeLogMode(fn: (next: FtLogMode) => void): () => void {
    listeners.add(fn);
    try { fn(mode); } catch { /* presentation only */ }
    return () => { listeners.delete(fn); };
}

/** iconMap() output wrapped for sizing, or null when the icon registry is not up yet. */
function glyphEl(key: string, size: number): HTMLElement | null {
    const iconMapFn = (window as unknown as { iconMap?: (k: string, w: number, h: number, t: string | boolean) => string }).iconMap;
    if (typeof iconMapFn !== 'function') return null;
    const html = iconMapFn(key, size, size, false);
    if (!html) return null;
    const el = document.createElement('span');
    el.className = 'ft3d-hud__event-icon';
    el.innerHTML = html;
    return el;
}

interface TooltipInstance { dispose?: () => void; hide?: () => void; }
interface TooltipStatic {
    new (el: Element, options?: Record<string, unknown>): TooltipInstance;
    getInstance?: (el: Element) => TooltipInstance | null;
    getOrCreateInstance?: (el: Element, options?: Record<string, unknown>) => TooltipInstance;
}

function tooltipStatic(): TooltipStatic | undefined {
    return (window as unknown as { bootstrap?: { Tooltip?: TooltipStatic } }).bootstrap?.Tooltip;
}

function applyTooltip(el: HTMLElement, text: string | undefined, placement?: string): void {
    if (!text) return;
    el.setAttribute('data-bs-toggle', 'tooltip');
    el.setAttribute('data-bs-original-title', text);
    el.setAttribute('title', text);
    if (placement) el.setAttribute('data-bs-placement', placement);
}

function mountTooltips(root: HTMLElement): void {
    const Tooltip = tooltipStatic();
    if (!Tooltip) return;
    root.querySelectorAll<HTMLElement>('[data-bs-toggle="tooltip"]').forEach((el) => {
        try {
            if (Tooltip.getOrCreateInstance) Tooltip.getOrCreateInstance(el, { boundary: 'window', trigger: 'hover focus' });
            else if (!Tooltip.getInstance?.(el)) new Tooltip(el, { boundary: 'window', trigger: 'hover focus' });
        } catch { /* a tooltip can never be allowed to break the log */ }
    });
}

function disposeTooltips(root: HTMLElement): void {
    const Tooltip = tooltipStatic();
    if (!Tooltip?.getInstance) return;
    root.querySelectorAll<HTMLElement>('[data-bs-toggle="tooltip"]').forEach((el) => {
        try {
            // dispose() alone: it removes a shown tip itself. hide() first queued a fade-out whose
            // completion ran AFTER dispose nulled the instance → Bootstrap's _isWithActiveTrigger threw
            // "Cannot convert undefined or null to object" whenever a focused/hovered control's line
            // expired (a clicked ADV/DIS toggle keeps focus, so this became routine).
            Tooltip.getInstance?.(el)?.dispose?.();
        } catch { /* detached presentation only */ }
    });
}

/**
 * The roller, as the owner chose: portrait + clickable name. The portrait carries the SAME
 * open-item-info contract as any other reference, so clicking it is handled by the log bundle's
 * existing delegated handler — no new authority.
 */
function actorEl(actor: FtLogActor): HTMLElement {
    const wrap = document.createElement('span');
    wrap.className = 'ft3d-hud__actor';

    const avatar = document.createElement('span');
    avatar.className = 'ft3d-hud__avatar';
    if (actor.picUrl) {
        const img = document.createElement('img');
        img.src = actor.picUrl;
        img.alt = '';
        avatar.appendChild(img);
    } else {
        // Reuse the initials the log renderer already chose rather than inventing our own.
        avatar.classList.add('is-initials');
        avatar.textContent = actor.initials || '';
    }
    wrap.appendChild(avatar);

    if (actor.name) {
        const name = document.createElement('span');
        name.className = 'ft3d-hud__seg ft3d-hud__seg--ref ft3d-hud__actor-name';
        name.textContent = actor.name;
        wrap.appendChild(name);
    }

    if (actor.itemId) {
        wrap.setAttribute('role', 'button');
        wrap.setAttribute('tabindex', '0');
        wrap.setAttribute('data-ft-log-action', 'open-item-info');
        wrap.setAttribute('data-ft-item-id', actor.itemId);
    }
    applyTooltip(wrap, actor.name);
    return wrap;
}

/**
 * The fold-out detail. Everything in it was taken from the row markup the page already had, so
 * opening a row costs no request and no second copy of the campaign data — it is only what the
 * collapsed line chose not to show.
 */
function detailEl(entry: FtLogEntry): HTMLElement {
    const detail = document.createElement('span');
    detail.className = 'ft3d-hud__detail';

    if (entry.detailText) {
        const text = document.createElement('span');
        text.className = 'ft3d-hud__detail-text';
        // The collapsed line elides; this is the whole thing, and untruncated names with it.
        text.textContent = entry.detailText;
        detail.appendChild(text);
    }

    const footer = timeFooterEl(entry);
    if (footer) detail.appendChild(footer);

    return detail;
}

/**
 * The Campaign / Real time band. Null when the entry carries neither reading. A grouped anchor's
 * copy of this band is re-homed under its last child (see applyGroupRun) so the time closes the
 * whole nested event instead of sitting between the anchor and the rolls it owns (owner 2026-09-14).
 */
function timeFooterEl(entry: FtLogEntry): HTMLElement | null {
    const footer = document.createElement('span');
    footer.className = 'ft3d-hud__detail-footer';
    const stamp = (label: string, value: string): void => {
        const cell = document.createElement('span');
        cell.className = 'ft3d-hud__detail-stamp';
        const key = document.createElement('span');
        key.className = 'ft3d-hud__detail-key';
        key.textContent = label;
        cell.appendChild(key);
        // A real space, not a margin — textContent is what a screen reader announces.
        cell.appendChild(document.createTextNode(` ${value}`));
        // Same between stamps ("… PM Real …", not "… PMReal …"); flex ignores whitespace-only text.
        if (footer.childElementCount > 0) footer.appendChild(document.createTextNode(' '));
        footer.appendChild(cell);
    };
    if (entry.campaignTime) stamp('Campaign', entry.campaignTime);
    if (entry.realTime) stamp('Real', entry.realTime);
    return footer.childElementCount > 0 ? footer : null;
}

function segmentEl(seg: FtLogSegment): HTMLElement {
    const el = document.createElement('span');
    el.className = `ft3d-hud__seg ft3d-hud__seg--${seg.kind}`;
    if (seg.kind === 'adv-die' || seg.kind === 'dis-die') {
        const toneLabel = seg.kind === 'adv-die' ? 'Advantage' : 'Disadvantage';
        el.classList.add(seg.isKept ? 'is-kept' : 'is-discarded');
        el.setAttribute('data-tone', seg.kind === 'adv-die' ? 'adv' : 'dis');
        el.setAttribute('aria-label', `${toneLabel} ${seg.isKept ? 'selected' : 'discarded'} die: ${seg.text}`);
        const value = document.createElement('span');
        value.className = 'ft3d-hud__adv-dis-value';
        value.textContent = seg.text;
        el.appendChild(value);
    } else {
        el.textContent = seg.text;
    }
    if (seg.kind === 'ref' && seg.itemId) {
        // The log bundle already delegates [data-ft-log-action] on the document, so this is
        // clickable through the existing authority — no listener is added here.
        el.setAttribute('role', 'button');
        el.setAttribute('tabindex', '0');
        el.setAttribute('data-ft-log-action', 'open-item-info');
        el.setAttribute('data-ft-item-id', seg.itemId);
    }
    if (seg.colorHex) el.style.setProperty('--ft3d-hud-seg-color', seg.colorHex);
    applyTooltip(el, seg.tooltip, seg.tooltipPlacement);
    return el;
}

/** A clickable reference using the log bundle's delegated open-item-info contract. */
function refEl(text: string, itemId: string | undefined, className: string): HTMLElement {
    const el = document.createElement('span');
    el.className = className;
    el.textContent = text;
    if (itemId) {
        el.classList.add('ft3d-hud__seg--ref');
        el.setAttribute('role', 'button');
        el.setAttribute('tabindex', '0');
        el.setAttribute('data-ft-log-action', 'open-item-info');
        el.setAttribute('data-ft-item-id', itemId);
    }
    return el;
}

function advDisSourceEl(source: FtLogAdvDisSource): HTMLElement {
    const row = document.createElement('span');
    row.className = 'ft3d-hud__adv-dis-source';
    row.setAttribute('data-tone', source.delta > 0 ? 'adv' : 'dis');

    const delta = document.createElement('span');
    delta.className = 'ft3d-hud__adv-dis-delta';
    delta.textContent = source.delta > 0 ? `+${source.delta}` : String(source.delta);
    row.appendChild(delta);

    const main = document.createElement('span');
    main.className = 'ft3d-hud__adv-dis-source-main';
    const name = document.createElement('span');
    name.className = 'ft3d-hud__adv-dis-source-name';
    const icon = source.itemId ? glyphEl(source.itemId, 14) : null;
    if (icon) name.appendChild(icon);
    name.appendChild(refEl(source.label || 'Unknown source', source.itemId, 'ft3d-hud__adv-dis-source-label'));
    main.appendChild(name);
    if (source.originName) {
        const origin = document.createElement('span');
        origin.className = 'ft3d-hud__adv-dis-origin';
        if (source.originThumb) {
            const img = document.createElement('img');
            img.src = source.originThumb;
            img.alt = '';
            img.className = 'ft3d-hud__adv-dis-origin-thumb';
            origin.appendChild(img);
        }
        origin.appendChild(refEl(source.originName, source.originItemId, 'ft3d-hud__adv-dis-origin-name'));
        main.appendChild(origin);
    }
    row.appendChild(main);

    if (source.kindIcon || source.kindLabel) {
        const kind = source.kindIcon ? glyphEl(source.kindIcon, 16) : null;
        const badge = kind || document.createElement('span');
        badge.className = kind ? 'ft3d-hud__adv-dis-kind' : 'ft3d-hud__adv-dis-kind is-text';
        if (!kind) badge.textContent = source.kindLabel || '';
        applyTooltip(badge, source.kindLabel, 'left');
        row.appendChild(badge);
    }
    return row;
}

/**
 * The legacy log's ADV/DIS trace: an adv / dis / cancelled icon beside the dice that folds out the
 * reasons (owner 2026-09-14 — "cannot see why a target has disadvantage"). Its own toggle, separate
 * from the row's time/detail fold-out, exactly as the legacy row kept them separate.
 */
function advDisTraceEls(trace: FtLogAdvDisTrace): { toggle: HTMLElement; panel: HTMLElement } {
    const tone = trace.net > 0 ? 'adv' : trace.net < 0 ? 'dis' : 'adv_dis';
    const toneLabel = trace.net > 0 ? 'Advantage' : trace.net < 0 ? 'Disadvantage' : 'Advantage and disadvantage cancelled';
    const count = trace.sources.length;

    const panel = document.createElement('span');
    panel.className = 'ft3d-hud__adv-dis-sources';
    panel.hidden = true;
    trace.sources.forEach((source) => panel.appendChild(advDisSourceEl(source)));

    const toggle = document.createElement('span');
    toggle.className = 'ft3d-hud__adv-dis-trace-toggle';
    toggle.setAttribute('data-tone', tone);
    toggle.setAttribute('role', 'button');
    toggle.setAttribute('tabindex', '0');
    const glyph = glyphEl(tone, 16);
    if (glyph) toggle.appendChild(glyph);
    else toggle.textContent = trace.net > 0 ? 'ADV' : trace.net < 0 ? 'DIS' : 'ADV/DIS';
    applyTooltip(toggle, 'ADV/DIS sources — click to expand');

    const setOpen = (open: boolean): void => {
        panel.hidden = !open;
        toggle.classList.toggle('is-open', open);
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        toggle.setAttribute('aria-label', `${toneLabel}: ${count} ${count === 1 ? 'source' : 'sources'} ${open ? 'shown' : 'hidden'}`);
        // Reveal just the opened panel — never yank an older row's reader down to the newest line.
        if (open && mode === 'on') panel.scrollIntoView({ block: 'nearest' });
    };
    setOpen(false);
    toggle.addEventListener('click', (event) => {
        event.stopPropagation();
        setOpen(!!panel.hidden);
    });
    toggle.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
        event.preventDefault();
        event.stopPropagation();
        setOpen(!!panel.hidden);
    });
    return { toggle, panel };
}

function isAdvDisSegment(seg: FtLogSegment | undefined): boolean {
    return seg?.kind === 'adv-die' || seg?.kind === 'dis-die';
}

/** One selected die plus the discarded alternatives that belong to that exact ADV/DIS pool. */
function advDisPoolEnd(segments: FtLogSegment[], start: number): number {
    const first = segments[start];
    if (!isAdvDisSegment(first)) return start + 1;
    let end = start + 1;
    while (end < segments.length) {
        const next = segments[end];
        if (!next || !isAdvDisSegment(next) || next.kind !== first?.kind || next.isKept) break;
        end += 1;
    }
    return end;
}

/**
 * Compact by default: `{ selected }`. Activating the braces reveals the discarded evidence from
 * this pool only: `{ selected discarded… }`. This keeps multi-die Empowered damage readable without
 * sacrificing the exact rolls, and remains separate from the whole-row detail foldout.
 */
function advDisPoolEl(segments: FtLogSegment[]): HTMLElement {
    const pool = document.createElement('span');
    const tone = segments[0]?.kind === 'dis-die' ? 'dis' : 'adv';
    const toneLabel = tone === 'adv' ? 'Advantage' : 'Disadvantage';
    const selected = segments.find((seg) => seg.isKept) || segments[0];
    const discardedCount = segments.filter((seg) => !seg.isKept).length;
    pool.className = 'ft3d-hud__adv-dis-pool';
    pool.setAttribute('data-tone', tone);

    const brace = (text: string): HTMLElement => {
        const el = document.createElement('span');
        el.className = 'ft3d-hud__adv-dis-brace';
        el.setAttribute('aria-hidden', 'true');
        el.textContent = text;
        return el;
    };
    pool.appendChild(brace('{'));
    segments.forEach((seg) => pool.appendChild(segmentEl(seg)));
    pool.appendChild(brace('}'));

    const setExpanded = (expanded: boolean): void => {
        pool.classList.toggle('is-expanded', expanded);
        pool.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        const selectedText = selected?.text || 'unknown';
        const state = expanded ? 'shown' : 'hidden';
        pool.setAttribute('aria-label', `${toneLabel} pool. Selected ${selectedText}. ${discardedCount} discarded ${discardedCount === 1 ? 'die' : 'dice'} ${state}.`);
        pool.setAttribute('title', discardedCount > 0
            ? `${expanded ? 'Hide' : 'Show'} ${discardedCount} discarded ${toneLabel.toLowerCase()} ${discardedCount === 1 ? 'die' : 'dice'}`
            : `${toneLabel} selected die ${selectedText}`);
    };

    if (discardedCount > 0) {
        pool.classList.add('is-collapsible');
        pool.setAttribute('role', 'button');
        pool.setAttribute('tabindex', '0');
        setExpanded(false);
        pool.addEventListener('click', (event) => {
            event.stopPropagation();
            setExpanded(!pool.classList.contains('is-expanded'));
        });
        pool.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
            event.preventDefault();
            event.stopPropagation();
            setExpanded(!pool.classList.contains('is-expanded'));
        });
    } else {
        setExpanded(true);
    }
    return pool;
}

/**
 * A yellow result can be either a bare outcome ("Self Adjust V 18") or the total at the end of an
 * arithmetic breakdown. Only the latter needs an equals sign. The rendered, already-redacted
 * segments are the authority: seeing an operator before this result proves a calculation without
 * maintaining another roll-type list or exposing anything that the row hid.
 */
function resultCompletesCalculation(segments: FtLogSegment[], resultIndex: number): boolean {
    if (segments[resultIndex]?.kind !== 'result') return false;
    return segments.slice(0, resultIndex).some((seg) => (
        seg.kind === 'text' && /(^|\s)[+*×÷](?=\s|$)/.test(seg.text)
    ));
}

/** A chosen ADV/DIS die reads as a decision, not arithmetic: "ADV 18 2 → 18". */
function resultFollowsAdvDisPool(segments: FtLogSegment[], resultIndex: number): boolean {
    if (segments[resultIndex]?.kind !== 'result') return false;
    for (let i = resultIndex - 1; i >= 0; i--) {
        const seg = segments[i];
        if (!seg) continue;
        if (seg.kind === 'adv-die' || seg.kind === 'dis-die') return true;
        if (seg.kind !== 'text' || seg.text.trim() !== '') return false;
    }
    return false;
}

function contestFromRun(run: HTMLElement[]): FtLogContest | undefined {
    const seeded = run[0] ? entryByLine.get(run[0])?.contest : undefined;
    if (seeded) return seeded;

    const entries = run
        .map((line) => entryByLine.get(line))
        .filter((entry): entry is FtLogEntry => !!entry && isContestingEntry(entry) && !!entry.actor);
    const leftEntry = entries[0];
    if (!leftEntry) return undefined;
    const defenders = entries.slice(1);
    if (defenders.length === 0) return undefined;
    const rightEntry = defenders.slice().sort((a, b) => {
        if (a.resultValue === undefined && b.resultValue === undefined) return 0;
        if (a.resultValue === undefined) return 1;
        if (b.resultValue === undefined) return -1;
        return b.resultValue - a.resultValue;
    })[0];
    if (!rightEntry) return undefined;

    const known = leftEntry.resultValue !== undefined && rightEntry.resultValue !== undefined;
    const leftWon = known ? leftEntry.resultValue! >= rightEntry.resultValue! : false;
    const rightWon = known ? !leftWon : false;
    const difference = known ? leftEntry.resultValue! - rightEntry.resultValue! : undefined;
    return {
        left: {
            actor: leftEntry.actor,
            resultText: leftEntry.resultValue === undefined ? 'Hidden' : String(leftEntry.resultValue),
            resultValue: leftEntry.resultValue,
            won: leftWon,
        },
        right: {
            actor: rightEntry.actor,
            resultText: rightEntry.resultValue === undefined ? 'Hidden' : String(rightEntry.resultValue),
            resultValue: rightEntry.resultValue,
            won: rightWon,
        },
        difference,
    };
}

function isContestingEntry(entry: FtLogEntry): boolean {
    if (entry.kind === 'contesting_roll') return true;
    const catalogId = (window as unknown as {
        ROLLTYPE_CATALOG?: { contesting_roll?: { item_id?: unknown } };
    }).ROLLTYPE_CATALOG?.contesting_roll?.item_id;
    return catalogId != null && entry.kind === String(catalogId);
}

function contestSideEl(side: FtLogContest['left']): HTMLElement {
    const el = document.createElement('span');
    el.className = 'ft3d-hud__contest-side';
    if (side.won) el.classList.add('is-winner');
    if (side.actor) el.appendChild(actorEl(side.actor));
    const result = document.createElement('span');
    result.className = 'ft3d-hud__contest-result';
    result.textContent = side.resultText;
    el.appendChild(result);
    return el;
}

function renderContestSummary(anchor: HTMLElement, contest: FtLogContest): void {
    anchor.querySelector(':scope > .ft3d-hud__contest')?.remove();
    const summary = document.createElement('span');
    summary.className = 'ft3d-hud__contest';
    summary.appendChild(contestSideEl(contest.left));
    const versus = document.createElement('span');
    versus.className = 'ft3d-hud__contest-vs';
    versus.textContent = 'VS';
    summary.appendChild(versus);
    summary.appendChild(contestSideEl(contest.right));
    const difference = document.createElement('span');
    difference.className = 'ft3d-hud__contest-difference';
    if (contest.difference !== undefined) {
        difference.classList.add(contest.difference >= 0 ? 'is-positive' : 'is-negative');
        difference.textContent = `${contest.difference >= 0 ? '+' : ''}${contest.difference}`;
    } else {
        difference.textContent = 'hidden';
    }
    summary.appendChild(difference);
    const detail = anchor.querySelector(':scope > .ft3d-hud__detail');
    anchor.insertBefore(summary, detail);
    anchor.classList.add('has-contest-summary');
}

const OWN_ROLL_PART_CLASSES = ['ft3d-hud__actor', 'ft3d-hud__event-icon', 'ft3d-hud__body'];

/**
 * Gather a live contest anchor's own roll (actor, event glyph, dice) into one sub-row so, opened, it
 * reads as a sibling of the defender child rows. The parts are moved, not cloned — their ref clicks
 * and tooltips stay wired — and the wrapper takes their exact slot so unwrapping restores the order.
 */
function wrapContestOwnRoll(anchor: HTMLElement): void {
    if (anchor.querySelector(':scope > .ft3d-hud__contest-own')) return;
    const parts = Array.from(anchor.children)
        .filter((el) => OWN_ROLL_PART_CLASSES.some((cls) => el.classList.contains(cls)));
    const first = parts[0];
    if (!first) return;
    const wrap = document.createElement('span');
    wrap.className = 'ft3d-hud__contest-own';
    anchor.insertBefore(wrap, first);
    parts.forEach((part) => wrap.appendChild(part));
}

function clearContestSummary(line: HTMLElement): void {
    const wrap = line.querySelector(':scope > .ft3d-hud__contest-own');
    if (wrap) wrap.replaceWith(...Array.from(wrap.childNodes));
    line.classList.remove('has-contest-summary', 'has-contest-own-roll');
    line.querySelector(':scope > .ft3d-hud__contest')?.remove();
}

const GROUP_LINE_CLASSES = [
    'is-grouped',
    'is-group-start',
    'is-group-end',
    'is-group-anchor',
    'is-group-child',
    'is-group-open',
    'is-group-collapsed',
];

function lineRollId(line: Element | null): string {
    return line?.getAttribute('data-ft3d-roll-id') || '';
}

/** All adjacent direct HUD rows that belong to the same server-authored roll_id event. */
function groupRunFor(line: HTMLElement): HTMLElement[] {
    const rollId = lineRollId(line);
    if (!rollId || !logEl || line.parentElement !== logEl) return [line];

    let first = line;
    while (first.previousElementSibling instanceof HTMLElement
        && lineRollId(first.previousElementSibling) === rollId
    ) {
        first = first.previousElementSibling;
    }

    const run: HTMLElement[] = [];
    let cursor: HTMLElement | null = first;
    while (cursor && lineRollId(cursor) === rollId) {
        run.push(cursor);
        cursor = cursor.nextElementSibling as HTMLElement | null;
    }
    return run;
}

function ensureExpandable(line: HTMLElement): void {
    line.classList.add('is-expandable');
    line.tabIndex = 0;
}

function updateExpandableState(line: HTMLElement): void {
    if (!line.classList.contains('is-expandable')) return;
    const open = line.classList.contains('is-expanded');
    line.setAttribute('aria-expanded', open ? 'true' : 'false');
}

function setLineExpanded(line: HTMLElement, open: boolean): void {
    line.classList.toggle('is-expanded', open);
    const run = groupRunFor(line);
    if (mode === 'on' && run.length > 1 && run[0] === line) applyGroupRun(run, open);
    else updateExpandableState(line);
    if (open && mode === 'on' && logEl) logEl.scrollTop = logEl.scrollHeight;
}

function removeGroupPresentation(line: HTMLElement): void {
    GROUP_LINE_CLASSES.forEach((cls) => line.classList.remove(cls));
    line.querySelectorAll(':scope > .ft3d-hud__group-footer').forEach((el) => el.remove());
    line.hidden = false;
    clearContestSummary(line);
    updateExpandableState(line);
}

function applyGroupRun(run: HTMLElement[], open = false): void {
    run.forEach(removeGroupPresentation);
    if (run.length < 2) {
        return;
    }

    run.forEach((line) => line.classList.add('is-grouped'));
    const anchor = run[0];
    if (!anchor) return;
    anchor.classList.add('is-group-start');
    const last = run[run.length - 1];
    if (last) last.classList.add('is-group-end');

    // The fading ticker remains a flat, readable run: rows are short-lived and its nth-child
    // opacity/expiry rules intentionally operate on every roll. Always-on is the durable history
    // surface, so it gets the compact parent + expandable indented subtree.
    if (mode !== 'on') {
        return;
    }

    anchor.classList.add('is-group-anchor');
    anchor.classList.toggle('is-group-open', open);
    anchor.classList.toggle('is-group-collapsed', !open);
    anchor.classList.toggle('is-expanded', open);
    run.slice(1).forEach((child) => {
        child.classList.add('is-group-child');
        child.hidden = !open;
    });
    // The event's time is its footer: under the last nested row, not between the anchor and its
    // children. CSS hides the anchor's own band while the group is open.
    const anchorEntry = entryByLine.get(anchor);
    const groupFooter = anchorEntry ? timeFooterEl(anchorEntry) : null;
    if (groupFooter && last) {
        groupFooter.classList.add('ft3d-hud__group-footer');
        last.appendChild(groupFooter);
    }
    ensureExpandable(anchor);
    const contest = contestFromRun(run);
    if (contest) {
        renderContestSummary(anchor, contest);
        // A live run's anchor IS the first contestant's own roll; the summary hides its prose, so
        // the open group must bring it back or that side's dice are never shown (only the
        // defender's child row was). A seeded anchor is the legacy VS card itself, whose rolls
        // all arrive as children, so it has no own roll to restore.
        if (!anchorEntry?.contest) {
            anchor.classList.add('has-contest-own-roll');
            wrapContestOwnRoll(anchor);
        }
    }
    updateExpandableState(anchor);
    mountTooltips(anchor);
}

function refreshGroupFor(line: HTMLElement): void {
    const run = groupRunFor(line);
    const open = !!run[0]?.classList.contains('is-expanded');
    applyGroupRun(run, open);
}

/** Rebuild grouping after a mode switch, history seed, or fading-row removal. */
function refreshAllGroups(): void {
    if (!logEl) return;
    // Chat lines share the stream but belong to no roll group; grouping walks the roll lines only.
    const lines = rollLines();
    const openRollIds = new Set(lines
        .filter((line) => line.classList.contains('is-expanded'))
        .map((line) => lineRollId(line))
        .filter(Boolean));
    lines.forEach((line) => removeGroupPresentation(line));

    let i = 0;
    while (i < lines.length) {
        const first = lines[i];
        if (!first) break;
        const rollId = lineRollId(first);
        let end = i + 1;
        while (rollId && end < lines.length && lineRollId(lines[end] || null) === rollId) end += 1;
        const run = lines.slice(i, end);
        applyGroupRun(run, openRollIds.has(rollId));
        i = end;
    }
}

function trimLogTo(cap: number): void {
    if (!logEl) return;
    // Counts and retires ROLL lines only — chat keeps its own, longer budget (chat.ts), so a busy
    // fight can never scroll away what somebody said.
    let rolls = rollLines();
    while (rolls.length > cap && rolls[0]) {
        const first = rolls[0];
        const rollId = lineRollId(first);
        // Never strand hidden children by trimming only their collapsed anchor. A shared-roll_id
        // run is one event, so retire it as one event when it crosses the history cap.
        const retiring = rollId ? groupRunFor(first) : [first];
        retiring.forEach((line) => {
            clearExpiry(line);
            disposeTooltips(line);
            line.remove();
        });
        rolls = rollLines();
    }
}

function appendEntry(entry: FtLogEntry, opts: { animate?: boolean; before?: HTMLElement | null; trim?: boolean } = {}): HTMLElement | null {
    if (!logEl || mode === 'off') return null;
    const animate = opts.animate !== false;
    if (entry.rollId) seenRollIds.add(entry.rollId);

    const line = document.createElement('p');
    line.className = 'ft3d-hud__line';
    if (entry.redacted) line.classList.add('is-redacted');
    if (entry.kind) line.setAttribute('data-ft3d-log-kind', entry.kind);

    // WHO. The row had no attribution at all before — "Right now we don't know who made the roll".
    if (entry.actor) line.appendChild(actorEl(entry.actor));
    // WHAT, as a glyph standing in for a phrase like "Status Effect Applied".
    if (entry.eventIcon) {
        const icon = glyphEl(entry.eventIcon, 14);
        if (icon) line.appendChild(icon);
    }

    const body = document.createElement('span');
    body.className = 'ft3d-hud__body';
    for (let i = 0; i < entry.segments.length; i += 1) {
        const seg = entry.segments[i];
        if (!seg) continue;
        // A real space, not a CSS margin: the line's textContent is what a screen reader announces
        // and what the smoke asserts on, so the words must not run together.
        if (i > 0) body.appendChild(document.createTextNode(' '));
        if (isAdvDisSegment(seg)) {
            const end = advDisPoolEnd(entry.segments, i);
            body.appendChild(advDisPoolEl(entry.segments.slice(i, end)));
            i = end - 1;
            continue;
        }
        if (resultCompletesCalculation(entry.segments, i)) {
            const equals = document.createElement('span');
            equals.className = 'ft3d-hud__equals';
            equals.textContent = '=';
            body.appendChild(equals);
            body.appendChild(document.createTextNode(' '));
        } else if (resultFollowsAdvDisPool(entry.segments, i)) {
            const arrow = document.createElement('span');
            arrow.className = 'ft3d-hud__adv-dis-arrow';
            arrow.textContent = '→';
            body.appendChild(arrow);
            body.appendChild(document.createTextNode(' '));
        }
        body.appendChild(segmentEl(seg));
    }
    // WHY it had ADV/DIS — beside the dice, like the legacy row; the reasons fold out under the line.
    const advDisTrace = entry.advDis ? advDisTraceEls(entry.advDis) : null;
    if (advDisTrace) {
        if (body.childNodes.length > 0) body.appendChild(document.createTextNode(' '));
        body.appendChild(advDisTrace.toggle);
    }
    line.appendChild(body);
    // TO WHOM. Attacks, heals and status effects name a target the prose walk used to swallow.
    if (entry.target) {
        const arrow = document.createElement('span');
        arrow.className = 'ft3d-hud__arrow';
        arrow.textContent = '→';
        body.appendChild(document.createTextNode(' '));
        body.appendChild(arrow);
        body.appendChild(document.createTextNode(' '));
        const targetEl = actorEl(entry.target);
        targetEl.classList.add('is-target');
        body.appendChild(targetEl);
    }

    if (advDisTrace) line.appendChild(advDisTrace.panel);

    if (entry.revealRollId) {
        const reveal = document.createElement('button');
        reveal.type = 'button';
        reveal.className = 'btn btn-sm btn-outline-secondary py-0 px-1';
        reveal.textContent = 'Reveal';
        reveal.title = 'Make this roll public';
        reveal.dataset.ftLogAction = 'make-roll-public';
        reveal.dataset.ftRollId = entry.revealRollId;
        line.appendChild(reveal);
    }

    // ⛔ No clock on the collapsed line. Campaign time is not wanted on screen by default; it lives
    // in the detail footer below, one click away on hover.
    if (entry.campaignTime || entry.realTime || entry.detailText) {
        ensureExpandable(line);
        updateExpandableState(line);
        line.appendChild(detailEl(entry));
    }

    // The row is the generous hit target; item/ability/creature references keep their existing
    // click-through contract and never accidentally fold the event. The row itself carries the
    // expanded state and remains keyboard-operable; no separate visual control is necessary.
    line.addEventListener('click', (event) => {
        const target = event.target instanceof Element ? event.target : null;
        if (!target || !line.classList.contains('is-expandable')) return;
        if (target.closest('a, button, input, select, textarea, [role="button"], [data-ft-log-action], .ft3d-hud__group-footer, .ft3d-hud__adv-dis-sources')) return;
        const selection = window.getSelection?.();
        if (selection && !selection.isCollapsed) return;
        setLineExpanded(line, !line.classList.contains('is-expanded'));
    });
    line.addEventListener('keydown', (event) => {
        if (event.target !== line || (event.key !== 'Enter' && event.key !== ' ')) return;
        event.preventDefault();
        setLineExpanded(line, !line.classList.contains('is-expanded'));
    });

    if (entry.rollId) {
        line.setAttribute('data-ft3d-roll-id', entry.rollId);
    }

    entryByLine.set(line, entry);
    logEl.insertBefore(line, opts.before ?? null);
    refreshGroupFor(line);
    mountTooltips(line);

    const cap = mode === 'fade' ? FADE_MAX_LINES : STACK_MAX_LINES;
    if (opts.trim !== false) trimLogTo(cap);

    // Let the newest line settle before the fade-in so it reads as arriving, not blinking. Seeded
    // history skips that — it was not "arriving", it was already there.
    if (animate) window.requestAnimationFrame(() => line.classList.add('is-shown'));
    else line.classList.add('is-shown');
    if (mode === 'fade') scheduleExpiry(line);
    else logEl.scrollTop = logEl.scrollHeight;
    return line;
}

/**
 * The legacy log arrives on the page already populated — #div_sidelog_results carries the
 * server/buildLog-rendered history. Without this the HUD log opened EMPTY on every load and only
 * filled as new rolls pushed in, which in always-on mode reads as "the log doesn't show up".
 *
 * The legacy list is newest-FIRST (rows are prepended), so it is reversed before appending.
 */
let seeded = false;

/**
 * Every roll_id this surface has shown or deliberately skipped, so a reconnect can tell a row it MISSED
 * from history it already knew about. Primed from the legacy log the page loaded with, whatever the mode.
 */
const seenRollIds = new Set<string>();

/**
 * When the current reconnect began (performance.now()). Lines appended live after it are newer than the repaint's
 * snapshot can be; older lines missing from the snapshot simply fell out of its window.
 */
let resyncStartedAt = Number.NEGATIVE_INFINITY;

/** The initial-history observer; a reconnect retires it so it cannot mark the gap's rows as already seen. */
let legacyLogObserver: MutationObserver | null = null;

function legacyLogRows(): Element[] {
    const list = document.querySelector('#div_sidelog_results > ul.list-group');
    return list ? Array.from(list.children) : [];
}

function primeSeenFromLegacyLog(): boolean {
    const rows = legacyLogRows();
    rows.forEach((row) => {
        const id = row.getAttribute('data-ft-roll-id') || '';
        if (id) seenRollIds.add(id);
    });
    return rows.length > 0;
}

/**
 * A dropped socket loses every roll_insert in the gap. The legacy log repaints from the server once the
 * roll channel is subscribed again (websocket/setup.ts → `ft:log-resynced`); this surface used to seed
 * only while EMPTY, so whatever landed in the gap — a GM's haven toggle, a damage row — never reached it.
 * Retained lines keep their controls and lifetimes; only missed events are projected. No
 * sound: these are history, and their cue already played or was lost with the frame.
 */
function reconcileAfterResync(): void {
    if (!logEl || mode === 'off') return;
    const rows = legacyLogRows();
    const events = renderedLogEvents(rows);
    const missed = events.filter(event => event.rollId !== '' && !seenRollIds.has(event.rollId));
    if (mode === 'on' && missed.length > 0) {
        const snapshotIds = new Set(events.map(event => event.rollId));
        const retained = new Map<string, HTMLElement>();
        let before: HTMLElement | null = null;
        rollLines().forEach(line => {
            const id = lineRollId(line);
            if (snapshotIds.has(id)) {
                if (!retained.has(id)) retained.set(id, line);
            } else if ((entryByLine.get(line)?.at ?? -1) >= resyncStartedAt) {
                before ??= line;
            } else {
                clearExpiry(line); disposeTooltips(line); line.remove();
            }
        });
        // Newest-first traversal inserts each missing event before its newer neighbour. Retained
        // runs (including open contests) never move or rehydrate; live-only rows stay at the tail.
        for (const event of events) {
            const held = retained.get(event.rollId);
            if (held) { before = held; continue; }
            if (seenRollIds.has(event.rollId) || !event.rollId) continue;
            const entries = projectRenderedGroup(event.row, event.pairedAnchor);
            let first: HTMLElement | null = null;
            entries.forEach(entry => {
                const line = appendEntry(entry, { animate: false, before, trim: false });
                first ??= line;
            });
            before = first ?? before;
        }
        seeded = true;
        trimLogTo(STACK_MAX_LINES);
    } else if (mode === 'fade') {
        missed.slice(0, FADE_MAX_LINES).reverse().forEach(event => {
            projectRenderedGroup(event.row, event.pairedAnchor).forEach(entry => appendEntry(entry));
        });
    }
    primeSeenFromLegacyLog();
}

/** Preserve the renderer's event boundaries before reversing its newest-first history. */
function renderedLogEvents(rows: Element[]): Array<{
    row: Element; pairedAnchor?: Element; rollId: string;
}> {
    const events: Array<{ row: Element; pairedAnchor?: Element; rollId: string }> = [];
    for (let i = 0; i < rows.length; i += 1) {
        const row = rows[i];
        if (!row) continue;
        const next = rows[i + 1];
        const paired = next && (next.classList.contains('death-log-session-rollup')
            || next.classList.contains('trade-log-trade-rollup')) ? next : undefined;
        const source = paired ?? row;
        events.push({ row: source, pairedAnchor: paired ? row : undefined,
            rollId: source.getAttribute('data-ft-roll-id') || row.getAttribute('data-ft-roll-id') || '' });
        if (paired) i += 1;
    }
    return events;
}

/** Reveal delivery has already patched the legacy group; reproject only that event. */
function updateRenderedRoll(event: Event): void {
    if (!logEl || mode === 'off') return;
    const rollId = String((event as CustomEvent<{ row: { roll_id?: unknown } }>).detail?.row?.roll_id ?? '');
    if (!rollId) return;
    const source = renderedLogEvents(legacyLogRows()).find(row => row.rollId === rollId);
    if (!source) return;
    const old = rollLines().filter(line => lineRollId(line) === rollId);
    const before = (old[old.length - 1]?.nextElementSibling ?? null) as HTMLElement | null;
    const open = old.some(line => line.classList.contains('is-expanded'));
    old.forEach(line => { clearExpiry(line); disposeTooltips(line); line.remove(); });
    projectRenderedGroup(source.row, source.pairedAnchor).forEach(entry => {
        const line = appendEntry(entry, { animate: false, before, trim: false });
        if (line && open) setLineExpanded(line, true);
    });
    trimLogTo(mode === 'fade' ? FADE_MAX_LINES : STACK_MAX_LINES);
}

/**
 * History belongs to ALWAYS-ON only.
 *
 * ⛔ Do not seed in fading mode. Fading is a live ticker: every line it holds is given an expiry, so
 * seeded history was scheduled for deletion the moment it arrived and the log was empty again ~14s
 * after load. That is exactly the "I turn on the campaign log and don't see anything" report — the
 * rows had been and gone before the viewer looked.
 */
function seedFromLegacyLog(): boolean {
    if (seeded || !logEl || mode !== 'on') return false;
    const list = document.querySelector('#div_sidelog_results > ul.list-group');
    if (!list || list.childElementCount === 0) return false;
    seeded = true;
    const events: FtLogEntry[][] = [];
    // Rows that already arrived LIVE before the history landed (an empty-history campaign: the first live row is
    // what makes the legacy list non-empty). Skip their history copy, and keep them below the history they follow.
    const liveLines = rollLines();
    const liveIds = new Set(liveLines.map((line) => lineRollId(line)).filter(Boolean));

    for (const event of renderedLogEvents(Array.from(list.children))) {
        const entries = projectRenderedGroup(event.row, event.pairedAnchor);
        if (entries.length > 0) events.push(entries);
        if (events.length >= SEED_MAX_ROWS) break;
    }
    events.reverse().forEach(entries => {
        if (entries[0]?.rollId && liveIds.has(entries[0].rollId)) return;
        entries.forEach((entry) => appendEntry(entry, { animate: false }));
    });
    liveLines.forEach((line) => logEl?.appendChild(line));
    refreshAllGroups();
    if (logEl) logEl.scrollTop = logEl.scrollHeight;
    return true;
}

/**
 * Switching TO always-on must show the backlog, whether or not it was ever seeded — the viewer is
 * asking to see what happened. Re-seeds when the stack is empty, including after a fading session
 * expired everything it held.
 */
function seedOnDemand(): void {
    if (!logEl || mode !== 'on') return;
    if (logEl.childElementCount > 0) return;
    seeded = false;
    seedFromLegacyLog();
}

function retireLegacyLogObserver(): void {
    legacyLogObserver?.disconnect();
    legacyLogObserver = null;
}

function watchForLegacyLog(): void {
    // Prime the reconnect bookkeeping in every mode; seeding stays always-on only.
    const primed = primeSeenFromLegacyLog();
    if (seedFromLegacyLog() || (primed && mode !== 'on')) return;
    const host = document.getElementById('div_sidelog_results');
    if (!host || typeof MutationObserver !== 'function') return;
    // buildLog() may still be in flight at mount; take the history as soon as it lands, once.
    const observer = new MutationObserver(() => {
        const landed = primeSeenFromLegacyLog();
        if (seedFromLegacyLog() || (landed && mode !== 'on')) retireLegacyLogObserver();
    });
    legacyLogObserver = observer;
    observer.observe(host, { childList: true, subtree: true });
    window.setTimeout(retireLegacyLogObserver, 20000);
}

export function mountFt3dLog(root: HTMLElement): void {
    logEl = root.querySelector<HTMLElement>('[data-ft3d-log]');
    // The toggle may be a rim tab in the route's own chrome rather than a HUD dock button, so fall
    // back to a document lookup — one control either way, wherever the route chose to put it.
    toggleEl = root.querySelector<HTMLElement>('[data-ft3d-log-mode-toggle]')
        ?? document.querySelector<HTMLElement>('[data-ft3d-log-mode-toggle]');
    mode = readMode();

    if (toggleEl) toggleEl.addEventListener('click', cycleLogMode);
    if (logEl) {
        logEl.addEventListener('pointerenter', () => { pinned = true; });
        logEl.addEventListener('pointerleave', () => { pinned = false; });
        // The log bundle's delegated [data-ft-log-action] handler is click-only, so a keyboard user
        // could focus a reference but never open it. Forward Enter/Space as a click rather than
        // duplicating the action table — the delegated handler stays the one authority.
        logEl.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
            const target = event.target instanceof Element
                ? event.target.closest<HTMLElement>('[data-ft-log-action="open-item-info"]')
                : null;
            if (!target) return;
            event.preventDefault();
            target.click();
        });
    }

    document.addEventListener('ft-roll-authorized', (event) => {
        const liveRollId = String(((event as CustomEvent).detail as { roll?: { roll_id?: unknown } } | null)?.roll?.roll_id ?? '');
        if (liveRollId) seenRollIds.add(liveRollId);
        if (mode === 'off') return;
        const entry = projectAuthorizedRoll((event as CustomEvent).detail);
        if (entry) appendEntry(entry);
    });
    window.addEventListener('ft:log-resynced', reconcileAfterResync);
    window.addEventListener('ft:roll-row-updated', updateRenderedRoll);
    // A reconnect repaint is not initial history: stop the mount-time observer before it lands.
    window.addEventListener('ft:ws-resubscribed', () => {
        resyncStartedAt = performance.now();
        retireLegacyLogObserver();
    });

    applyMode();
    watchForLegacyLog();
    // iconMap ships in a later bundle on some routes; re-render once it exists.
    window.setTimeout(renderToggleGlyph, 0);
    window.addEventListener('load', renderToggleGlyph, { once: true });
}
